import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

export const useReportsFilters = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize selected projects from URL
    const [selectedProjects, setSelectedProjects] = useState<string[]>(() => {
        const searchParams = new URLSearchParams(location.search);
        const projectsParam = searchParams.get('projects');
        return projectsParam ? projectsParam.split(',').filter(Boolean) : [];
    });

    // Initialize dates from URL
    const [startDate, setStartDate] = useState<string | undefined>(() => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('startDate') || undefined;
    });

    const [endDate, setEndDate] = useState<string | undefined>(() => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('endDate') || undefined;
    });

    const locationRef = useRef(location);
    const navigateRef = useRef(navigate);

    locationRef.current = location;
    navigateRef.current = navigate;

    // Sync state from URL when location changes (e.g., when switching tabs)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        const projectsParam = searchParams.get('projects');
        const newSelectedProjects = projectsParam ? projectsParam.split(',').filter(Boolean) : [];

        const newStartDate = searchParams.get('startDate') || undefined;
        const newEndDate = searchParams.get('endDate') || undefined;

        setSelectedProjects(newSelectedProjects);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }, [location.search]);

    // Sync filters to URL
    useEffect(() => {
        const queryParams = new URLSearchParams(locationRef.current.search);
        let hasChanges = false;

        // Handle projects
        const currentProjectsParam = queryParams.get('projects') || '';
        const newProjectsValue = selectedProjects.join(',');
        if (currentProjectsParam !== newProjectsValue) {
            if (selectedProjects.length > 0) {
                queryParams.set('projects', newProjectsValue);
            } else {
                queryParams.delete('projects');
            }
            hasChanges = true;
        }

        // Handle dates
        const currentStartDate = queryParams.get('startDate');
        const currentEndDate = queryParams.get('endDate');

        if (startDate && endDate) {
            if (currentStartDate !== startDate || currentEndDate !== endDate) {
                queryParams.set('startDate', startDate);
                queryParams.set('endDate', endDate);
                hasChanges = true;
            }
        } else if (currentStartDate || currentEndDate) {
            queryParams.delete('startDate');
            queryParams.delete('endDate');
            hasChanges = true;
        }

        if (hasChanges) {
            navigateRef.current(`${locationRef.current.pathname}?${queryParams.toString()}`, {
                replace: true
            });
        }
    }, [selectedProjects, startDate, endDate]);

    const handleProjectChange = useCallback((value: string) => {
        setSelectedProjects((prev) => {
            const isSelected = prev.includes(value);
            return isSelected ? prev.filter((item) => item !== value) : [...prev, value];
        });
    }, []);

    const handleDateChange = useCallback((newStartDate: string, newEndDate: string) => {
        if (!newStartDate || !newEndDate) {
            setStartDate(undefined);
            setEndDate(undefined);
        } else {
            setStartDate(newStartDate);
            setEndDate(newEndDate);
        }
    }, []);

    const clearFiltersButton = useMemo(
        () => selectedProjects.length > 0 || (startDate !== undefined && endDate !== undefined),
        [selectedProjects, startDate, endDate]
    );

    const handleClearFilters = useCallback(() => {
        setSelectedProjects([]);
        setStartDate(undefined);
        setEndDate(undefined);
    }, []);

    return {
        selectedProjects,
        handleProjectChange,
        startDate,
        endDate,
        handleDateChange,
        clearFiltersButton,
        handleClearFilters
    };
};

